import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/styles';
import { Box } from '@mui/system';
import DialogCancelButton from 'ui-component/extended/DialogCancelButton';
import ChoosePlanIcon from 'common/svg/ChoosePlanIcon';
import CloseIcon from '@mui/icons-material/Close';
import getAxiosInstance from 'api';
import NgCurrencySymbol from 'common/svg/NgCurrencySymbol';
import { toast } from 'react-hot-toast';
import SkeletonPopularCard from 'ui-component/cards/Skeleton/PopularCard';
import {
  loadCreditRiskPermissionState,
  updateProductPermissions,
} from 'utils/productPermissions';
import { Grid } from 'rsuite';

const options = [
  { label: 'Charge User', id: 'Flat' },
  { label: 'Charge Wallet', id: 'Wallet' },
];

// Move styled components outside the functional component if they don’t rely on component state
const BoxHeaderWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px',
  borderRadius: '10px',
  margin: '10px 0 12px 0',
}));
const TwoColumnFormGroup = styled(FormGroup)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(1), // spacing between items
  alignItems: 'center', // vertical align if labels vary
}));
const OutlinedInputSet = styled(OutlinedInput)(({ theme }) => ({
  width: '115px',
  backgroundColor: '#ffffff',
  '& input': {
    backgroundColor: '#ffffff',
    color: '#000000',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    padding: '10px 0',
  },
}));
const TableCellPlanType = styled(TableCell)(({ theme }) => ({
  color: '#212120',
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '28px',
}));
const TableCellHeading = styled(TableCell)(({ theme }) => ({
  color: '#212120',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  opacity: 0.6,
}));

/** When free allowance is on, show free allowance balance until it is 0, then wallet balance. */
function getAvailableUnitDisplay(item) {
  if (!item.isFreeAllowanceAllowed) {
    return item.walletBalance;
  }
  const freeAllowanceBalance = item.freeAllowanceBalance;
  if (freeAllowanceBalance == null || Number(freeAllowanceBalance) === 0) {
    return item.walletBalance;
  }
  return freeAllowanceBalance;
}

/** True when the UI should show freeAllowanceBalance (not wallet) for this row. */
function isShowingFreeAllowanceBalance(item) {
  if (!item.isFreeAllowanceAllowed) return false;
  const freeAllowanceBalance = item.freeAllowanceBalance;
  if (freeAllowanceBalance == null) return false;
  if (Number(freeAllowanceBalance) === 0) return false;
  return true;
}

const ChoosePlan = ({ isOpen, handleClose, selected }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [productPermissionOptions, setProductPermissionOptions] = useState([]);
  const [transactionPermissionOptions, setTransactionPermissionOptions] =
    useState([]);
  const [selectedProductPermissions, setSelectedProductPermissions] = useState(
    []
  );
  const [selectedTransactionPermissions, setSelectedTransactionPermissions] =
    useState([]);
  const [pricingPlan, setPricingPlan] = useState({});
  const [creditRiskAssessment, setCreditRiskAssessment] = useState({});
  const [planData, setPlanData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const hasProductPlan = planData.some(
    (item) => item.productPlan && item.productCode === 'affordability_full'
  );
  const hasFreeAllowanceAllowed = planData.some(
    (item) => item.isFreeAllowanceAllowed === true
  );
  // Fetch pricing data when modal is opened
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      getAxiosInstance(
        `adminOrchestrator/businessPartner/${selected}/pricing`,
        'get',
        {},
        { 'journey-type': 'Business-PartnerDashboard' }
      ).then((res) => {
        const productsList = [];
        setPricingPlan(res?.data?.data);
        if (res?.data?.data?.products?.length) {
          res.data.data.products.forEach((item) => {
            productsList.push({
              ...item,
              // Store price and balance as strings for proper controlled input behavior.
              price: item.price !== undefined ? item.price.toString() : '',
              balanceToAdd:
                item.balanceToAdd !== undefined
                  ? item.balanceToAdd.toString()
                  : '',
              freeAllowanceUnits:
                item.freeAllowanceUnits !== undefined
                  ? item.freeAllowanceUnits.toString()
                  : '0',
              selectedPlanType: item?.pricingPlan
                ? options.find((option) => option.id === item.pricingPlan)
                : options[0],
              isActive: item?.isActive || false,
            });
          });
        }
        setPlanData(productsList);
        const creditRiskAssessment = productsList.find(
          (item) =>
            item.productCode === 'affordability_full' &&
            item?.productPlan === 'Platinum_Basic'
        );
        setCreditRiskAssessment(creditRiskAssessment);
        loadCreditRiskPermissionState({
          planId: creditRiskAssessment?.planId,
          entitlement: creditRiskAssessment,
          businessPartnerId: selected,
          journeyType: 'Business-PartnerDashboard',
        })
          .then(
            ({
              productPermissionOptions,
              transactionPermissionOptions,
              selectedProductPermissions,
              selectedTransactionPermissions,
            }) => {
              setProductPermissionOptions(productPermissionOptions);
              setTransactionPermissionOptions(transactionPermissionOptions);
              setSelectedProductPermissions(selectedProductPermissions);
              setSelectedTransactionPermissions(selectedTransactionPermissions);
            }
          )
          .catch(() => {
            toast.error('Something went to wrong');
          });
        setLoading(false);
      });
    }
  }, [isOpen, selected]);

  useEffect(() => {}, []);

  // Generalized input change handler
  const handleInputChange = (item, fieldName, value) => {
    setPlanData((prevData) =>
      prevData.map((unit) =>
        unit.productCode === item.productCode
          ? { ...unit, [fieldName]: value }
          : unit
      )
    );
  };

  // Handle dropdown change for plan type
  const handleDropdownChange = (productCode, newValue) => {
    setPlanData((prevData) =>
      prevData.map((item) =>
        item.productCode === productCode
          ? { ...item, selectedPlanType: newValue }
          : item
      )
    );
  };

  const handleSubmit = async () => {
    try {
      // Convert numeric fields from strings to numbers on submission
      const updatedPlanData = planData.map((item) => ({
        ...item,
        pricingPlan: item.selectedPlanType.id,
        price: parseFloat(item.price) || 0,
        balanceToAdd: parseFloat(item.balanceToAdd) || 0,
        freeAllowanceUnits: item.isFreeAllowanceAllowed
          ? parseFloat(item.freeAllowanceUnits) || 0
          : undefined,
      }));

      // If free allowance units were updated for an eligible product, call API
      const allowanceUpdates = updatedPlanData
        .filter(
          (item) =>
            item.isFreeAllowanceAllowed &&
            item.productEntitlementId !== undefined &&
            item.productEntitlementId !== null
        )
        .map((item) =>
          getAxiosInstance(
            'adminOrchestrator/businessPartner/updateFreeAllowanceUnits',
            'post',
            {
              freeAllowanceUnits: item.freeAllowanceUnits,
              productEntitlementId: item.productEntitlementId,
            },
            { 'journey-type': 'Business-PartnerDashboard' }
          )
        );

      if (allowanceUpdates.length > 0) {
        const allowanceResponses = await Promise.all(allowanceUpdates);
        const allowanceErrors = allowanceResponses.some(
          (res) => res?.data?.codes !== 200
        );
        if (allowanceErrors) {
          toast.error('Failed to update free allowance units.');
          return;
        }
      }

      const pricingResponse = await getAxiosInstance(
        `adminOrchestrator/businessPartner/${selected}/pricing`,
        'post',
        { productPricing: updatedPlanData },
        { 'journey-type': 'Business-PartnerDashboard' }
      );

      if (pricingResponse?.data?.codes !== 200) {
        toast.error(pricingResponse?.data?.message || 'Failed to update pricing');
        return;
      }

      const creditRiskAssessment = updatedPlanData.find(
        (item) =>
          item.productCode === 'affordability_full' &&
          item?.productPlan === 'Platinum_Basic'
      );

      if (creditRiskAssessment) {
        const permissionsResponse = await updateProductPermissions({
          productEntitlementId: creditRiskAssessment.productEntitlementId,
          permissions: selectedProductPermissions,
          transactionPermissions: selectedTransactionPermissions,
          journeyType: 'Business-PartnerDashboard',
        });

        if (permissionsResponse?.data?.codes !== 200) {
          toast.error(
            permissionsResponse?.data?.message ||
              'Failed to update permissions'
          );
          return;
        }
      }

      toast.success('Pricing updated successfully.');
      handleClose();
      setTimeout(() => {
        setPlanData([]);
        setSelectedProductPermissions([]);
        setSelectedTransactionPermissions([]);
      }, 1000);
    } catch (error) {
      toast.error('Something went wrong while saving pricing.');
    }
  };

  const handleProductPermission = (value) => {
    setSelectedProductPermissions(
      (prev) =>
        prev.includes(value)
          ? prev.filter((p) => p !== value) // remove if already selected
          : [...prev, value] // add if not selected
    );
  };

  const handleTransactionPermission = (value) => {
    setSelectedTransactionPermissions((prev) =>
      prev.includes(value)
        ? prev.filter((p) => p !== value)
        : [...prev, value]
    );
  };
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '1200px',
          width: '100%',
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                borderRadius: '10px',
                background: 'rgba(252, 87, 25, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px',
              }}
            >
              <ChoosePlanIcon color={theme.palette.secondary.main} />
            </Box>
            <Typography variant="h2" fontWeight={600}>
              Choose Plan
            </Typography>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            border: '1px solid #DEDEDE',
            borderRadius: '10px',
            overflow: 'auto',
            maxHeight: '900px',
            marginBottom: '20px',
          }}
        >
          <Table aria-labelledby="choose plan">
            <TableHead>
              <TableRow>
                <TableCellHeading sx={{ width: '250px' }}>
                  Product Name
                </TableCellHeading>
                {hasProductPlan && (
                  <TableCellHeading>Plan Type</TableCellHeading>
                )}
                <TableCellHeading sx={{ width: '200px' }}>
                  Plan
                </TableCellHeading>
                <TableCellHeading>
                  {planData.some(isShowingFreeAllowanceBalance)
                    ? 'Free Available Unit'
                    : 'Available Unit'}
                </TableCellHeading>
                <TableCellHeading>Price</TableCellHeading>
                <TableCellHeading>Quantity</TableCellHeading>
                {hasFreeAllowanceAllowed && (
                  <TableCellHeading>Free Allowance Units</TableCellHeading>
                )}
                <TableCellHeading>Total Amount</TableCellHeading>
              </TableRow>
            </TableHead>
            {!isLoading ? (
              <TableBody>
                {planData.length > 0 &&
                  planData.map((item) => {
                    // Parse string values for calculation; fallback to 0 if invalid.
                    const numericPrice = parseFloat(item.price) || 0;
                    const numericQuantity = parseFloat(item.balanceToAdd) || 0;
                    return (
                      <TableRow key={item.productCode}>
                        <TableCellPlanType>
                          {item.productName}
                        </TableCellPlanType>
                        {hasProductPlan && (
                          <TableCellPlanType>
                            {item.productPlan?.split('_')[0]}
                          </TableCellPlanType>
                        )}
                        <TableCell align="center" sx={{ width: '175px' }}>
                          <Autocomplete
                            options={options}
                            value={item.selectedPlanType}
                            renderInput={(params) => <TextField {...params} />}
                            onChange={(e, newValue) =>
                              handleDropdownChange(item.productCode, newValue)
                            }
                          />
                        </TableCell>
                        <TableCellPlanType align="center">
                          {getAvailableUnitDisplay(item)}
                        </TableCellPlanType>
                        <TableCell>
                          <OutlinedInputSet
                            type="number"
                            startAdornment={
                              <InputAdornment position="start">
                                <NgCurrencySymbol />
                                <Divider
                                  sx={{
                                    height: 20,
                                    m: 1,
                                    borderColor: '#dedede',
                                  }}
                                  orientation="vertical"
                                />
                              </InputAdornment>
                            }
                            value={item.price}
                            onChange={(e) =>
                              handleInputChange(item, 'price', e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <OutlinedInputSet
                            type="number"
                            startAdornment={
                              <InputAdornment position="start"></InputAdornment>
                            }
                            value={item.balanceToAdd || 0}
                            onChange={(e) =>
                              handleInputChange(
                                item,
                                'balanceToAdd',
                                e.target.value
                              )
                            }
                          />
                        </TableCell>

                        {hasFreeAllowanceAllowed && (
                          <TableCell>
                            {item.isFreeAllowanceAllowed ? (
                              <OutlinedInputSet
                                type="number"
                                startAdornment={
                              <InputAdornment position="start"></InputAdornment>
                            }
                                value={item.freeAllowanceUnits || 0}
                                onChange={(e) =>
                                  handleInputChange(
                                    item,
                                    'freeAllowanceUnits',
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              <Typography sx={{ color: '#808080' }}>
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        <TableCellPlanType align="center">
                          <NgCurrencySymbol />
                          {numericPrice * numericQuantity}
                        </TableCellPlanType>
                      </TableRow>
                    );
                  })}
              </TableBody>
            ) : (
              <SkeletonPopularCard />
            )}
          </Table>
        </Box>
        {creditRiskAssessment && (
          <>
            <Typography variant="h2" fontWeight={600}>
              Select Feature
            </Typography>
            <Box
              sx={{
                border: '1px solid #DEDEDE',
                borderRadius: '10px',
                overflow: 'auto',
                maxHeight: '900px',
                padding: '20px',
                marginTop: '20px',
              }}
            >
              <Grid item xs={12}>
                <TwoColumnFormGroup>
                  {productPermissionOptions.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      control={
                        <Checkbox
                          checked={selectedProductPermissions.includes(opt)}
                          onChange={() => handleProductPermission(opt)}
                          color="secondary"
                        />
                      }
                      label={opt}
                    />
                  ))}
                </TwoColumnFormGroup>
              </Grid>
            </Box>

            <Typography variant="h2" fontWeight={600} sx={{ mt: 3 }}>
              Select Raw Banking Data
            </Typography>
            <Box
              sx={{
                border: '1px solid #DEDEDE',
                borderRadius: '10px',
                overflow: 'auto',
                maxHeight: '900px',
                padding: '20px',
                marginTop: '20px',
              }}
            >
              <Grid item xs={12}>
                <TwoColumnFormGroup>
                  {transactionPermissionOptions.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      control={
                        <Checkbox
                          checked={selectedTransactionPermissions.includes(opt)}
                          onChange={() => handleTransactionPermission(opt)}
                          color="secondary"
                        />
                      }
                      label={opt}
                    />
                  ))}
                </TwoColumnFormGroup>
              </Grid>
            </Box>
          </>
        )}

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          mt={3.5}
        >
          <DialogCancelButton variant="contained" onClick={handleClose}>
            Cancel
          </DialogCancelButton>

          <Button variant="contained" color="secondary" onClick={handleSubmit}>
            Save
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ChoosePlan;
